import * as React from "react";
import * as S from "@styled";
import { ElementImportEntry, ElementImportViewModel, ImportConflictOption, ImportSettings } from "../model/ImportModel";
import CustomModal from "./CustomModal";
import { ImportEntry } from "./ImportEntry";
import { Option } from "shared/model/Option";

export class ImportModal extends React.Component<ImportModalProps, ImportModalState> {
    constructor(props: ImportModalProps) {
        super(props);
        this.state = {
            importSettings: props.elementImportViewModel.importResults.map((process) => {
                const conflictOptionsList = this.getImportOptions(process);
                return {
                    uri: process.uri,
                    conflictOptionsList: conflictOptionsList,
                    conflictOption: conflictOptionsList[0].value // This should not be null or empty
                };
            }),
            isImporting: false
        };
        this.handleImportClick = this.handleImportClick.bind(this);
    }

    public render() {
        return (
            <CustomModal
                label="new-oauth-modal"
                onClose={this.props.onHide}
                size={"large"}
                show={true}
                shouldCloseOnOverlayClick={false}
                modalTitle={<h2>{FRENDS.Localizations.Import}</h2>}
            >
                <S.FormGroup>
                    {this.getMissingPackagesWarning()}
                    {this.getMissingSubprocessWarning()}
                    {this.getMissingEnvironmentVariablesWarning()}
                    {this.getImportEntries()}
                </S.FormGroup>
                <S.ButtonGroup gap={12} separate={true}>
                    <S.Button variant={S.ButtonType.bordered} onClick={this.props.onHide}>{FRENDS.Localizations.Close}</S.Button>
                    <S.Button
                        disabled={this.state.isImporting}
                        onClick={this.handleImportClick}
                    >
                        {FRENDS.Localizations.Import}
                    </S.Button>
                </S.ButtonGroup>
            </CustomModal>
        );
    }

    private getImportOptions(entry: ElementImportEntry): Array<Option<ImportConflictOption>> {
        const options: Array<Option<ImportConflictOption>> = [];
        if (entry.hasMissingEnvironmentVariables || entry.hasMissingSubprocess || entry.hasMissingTasks) {
            // Have to create a draft as process needs to be fixed for errors. 
            if (entry.hasConflict && !entry.isDeleted) { // Can not creata a draft on existing process
                options.push({
                    label: FRENDS.Localizations.ConflictOptionsExistingProcessDraft,
                    value: ImportConflictOption.ExistingDraft
                });
            }
            options.push({
                label: FRENDS.Localizations.ConflictOptionsNewDraft,
                value: ImportConflictOption.NewDraft
            });
        } else {
            if (entry.hasConflict) {
                // Process already exists
                options.push({
                    label: FRENDS.Localizations.ConflictOptionsNewVersion,
                    value: ImportConflictOption.NewVersion
                });
                options.push({
                    label: FRENDS.Localizations.ConflictOptionsUseExisting,
                    value: ImportConflictOption.UseExisting
                });
                if (!entry.isSubProcess) {
                    options.push({
                        label: FRENDS.Localizations.CreateNewProcessAndActivateIt,
                        value: ImportConflictOption.NewActiveElement
                    });
                }
                options.push({
                    label: FRENDS.Localizations.CreateNewProcess,
                    value: ImportConflictOption.NewInactiveElement
                });

            } else {
                // New process no existing processes
                if (!entry.isSubProcess) {
                    options.push({
                        label: FRENDS.Localizations.CreateNewProcessAndActivateIt,
                        value: ImportConflictOption.NewActiveElement
                    });
                }
                options.push({
                    label: FRENDS.Localizations.CreateNewProcess,
                    value: ImportConflictOption.NewInactiveElement
                });
            }
        }
        return options;
    }

    private getMissingPackagesWarning() {
        if (this.props.elementImportViewModel.allMissingTaskPackages) {
            return (
                <S.ErrorContainer>
                    <div className="alert-warning">
                        <div className="alert-label">
                            {FRENDS.Localizations.TasksNeedToBeImported}
                        </div>
                        <div className="alert-content">
                            {this.props.elementImportViewModel.allMissingTaskPackages}
                        </div>
                    </div>
                </S.ErrorContainer>
            );
        }
        return null;
    }

    private getMissingSubprocessWarning() {
        if (this.props.elementImportViewModel.allMissingSubProcesses) {
            return (
                <S.ErrorContainer>
                    <div className="alert-warning">
                        <div className="alert-label">
                            {FRENDS.Localizations.SubProcessNeedsToBeImported}
                        </div>
                        <div className="alert-content">
                            {this.props.elementImportViewModel.allMissingSubProcesses}
                        </div>
                    </div>
                </S.ErrorContainer>
            );
        }
        return null;
    }

    private getMissingEnvironmentVariablesWarning() {
        if (this.props.elementImportViewModel.allMissingEnvironmentVariables) {
            return (
                <S.ErrorContainer>
                    <div className="alert-warning">
                        <div className="alert-label">
                            {FRENDS.Localizations.HasNonExistingEnvironmentVariables}
                        </div>
                        <div className="alert-content">
                            {this.props.elementImportViewModel.allMissingEnvironmentVariables}
                        </div>
                    </div>
                </S.ErrorContainer>
            );
        }
        return null;
    }

    private changeImportOption = (option: Option, settingId: string) => {
        const state: ImportModalState = Object.assign({}, this.state);
        const selectedSetting = state.importSettings.find((i) => i.uri.id === settingId);
        if (selectedSetting !== undefined) {
            selectedSetting.conflictOption = option.value;
        }
        this.setState(state);
    }

    private getImportEntries() {
        return (
            <S.StyledTable columnCount={4}>
                <thead>
                    <tr>
                        <th>Type</th>
                        <th>{FRENDS.Localizations.Name}</th>
                        <th>{FRENDS.Localizations.Warning}</th>
                        <th>Create options</th>
                    </tr>
                </thead>
                <tbody>
                    {this.state.importSettings ? this.props.elementImportViewModel.importResults.map((entry) => {
                        const setting = this.state.importSettings.find((i) => i.uri.id === entry.uri.id);
                        return <ImportEntry
                            key={entry.uri.id}
                            entry={entry}
                            setting={setting}
                            onChangeImportOptions={this.changeImportOption}
                        />;
                    }) : null}
                </tbody>
            </S.StyledTable>
        );
    }
    private handleImportClick() {
        this.setState({ isImporting: true } as ImportModalState);
        try {
            this.props.onImport(this.state.importSettings);
            this.props.onHide();
        } catch (e) {
            console.log(JSON.stringify(e));
            this.setState({ isImporting: false } as ImportModalState);
        }
    }
}

export interface ImportModalState {
    importSettings: ImportSettings[];
    isImporting: boolean;
}

export interface ImportModalProps {
    onHide: () => void;
    elementImportViewModel: ElementImportViewModel;
    onImport: (importSettings: ImportSettings[]) => void;
}
